import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The owl wakes up: mark bobs in, lime sparks drift up, eyes blink. */
const OwlCheer = () => (
  <div className="relative h-28 flex items-end justify-center" data-testid="first-deposit-owl">
    {["a", "b", "c", "d", "e", "f", "g", "h", "i"].map((id, i) => (
      <span key={id} className="owl-spark"
        style={{ left: `${8 + i * 10.5}%`, animationDelay: `${i * 170}ms`, height: 6 + (i % 3) * 3 }} />
    ))}
    <img src="/tharwy-mark-light.png" alt="" className="relative h-24 w-24 object-contain owl-cheer" />
  </div>
);

export const FirstDepositCelebration = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const check = useCallback(async () => {
    try {
      const { data: d } = await api.get("/first-deposit/celebration");
      if (d.show) setData(d);
    } catch (e) { /* signed out or offline — nothing to celebrate */ }
  }, []);

  useEffect(() => {
    check();
    const onDeposit = () => setTimeout(check, 1200);
    window.addEventListener("tharwy:deposit", onDeposit);
    return () => window.removeEventListener("tharwy:deposit", onDeposit);
  }, [check]);

  const dismiss = async (then) => {
    setData(null);
    try { await api.post("/first-deposit/seen"); } catch (e) { /* it reappears next login at worst */ }
    if (then) navigate(then);
  };

  if (!data) return null;
  const s = data.suggestion || {};
  const top = (s.allocation || []).slice(0, 4);

  return (
    <Dialog open onOpenChange={() => dismiss()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-lg p-0 overflow-hidden" data-testid="first-deposit-dialog">
        <div className="night-sky px-7 pt-7 pb-6 text-center">
          <OwlCheer />
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--tharwy-lime)]">
            Your money has landed
          </div>
          <DialogTitle className="mt-2 font-heading text-2xl font-semibold text-white"
            data-testid="first-deposit-title">
            {data.name ? `Nicely done, ${data.name}.` : "Nicely done."}
          </DialogTitle>
          <p className="mt-2 text-sm text-white/70">
            <span className="numeric font-semibold text-white" data-testid="first-deposit-amount">
              ${fmt(data.deposit?.amount)}
            </span>{" "}
            is in your wallet. Here's a portfolio built from your answers — you can change it any time.
          </p>
        </div>

        <div className="px-7 py-6">
          <div className="flex items-baseline justify-between">
            <div className="font-heading font-semibold" data-testid="first-deposit-suggestion">{s.headline}</div>
            <div className="text-xs text-muted-foreground">
              {s.expected_return}% p.a. target · {s.expected_volatility}% swing
            </div>
          </div>

          <div className="mt-4 space-y-2.5">
            {top.map((a) => (
              <div key={a.symbol} data-testid={`first-deposit-alloc-${a.symbol}`}>
                <div className="flex justify-between text-xs">
                  <span className="font-semibold">{a.symbol} <span className="font-normal text-muted-foreground">{a.name}</span></span>
                  <span className="numeric text-muted-foreground">{Math.round(a.weight * 100)}%</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${a.weight * 100}%`, background: "var(--tharwy-lime)" }} />
                </div>
              </div>
            ))}
          </div>

          {s.halal && (
            <p className="mt-4 text-xs text-muted-foreground">
              Screened names only — every holding passes the AAOIFI thresholds.
            </p>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <Button className="flex-1 rounded-full" onClick={() => dismiss("/invest")}
              data-testid="first-deposit-invest-btn">
              Put it to work <ArrowRight className="h-4 w-4 ms-1.5" />
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => dismiss()}
              data-testid="first-deposit-later-btn">
              Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
